import { useEffect, useState } from 'react'
import Add from '@mui/icons-material/Add'
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useHorizontalDrag from '@/hooks/useHorizontalDrag'
import useOverlay from '@/hooks/useOverlay'
import useAllOrganizations from '@/services/hooks/useAllOrganizations'
import { OrgCredential, OrgCredentialType } from '@/types/credentials'
import { isAuthenticated } from '@/utils/org-credentials'
import AddCompanyDialog from './AddCompanyDialog'
import CredentialTypeProvider from './CredentialTypeProvider'
import PaymentMethodsSettings from './PaymentMethodsSettings'
import VendorSettings from './VendorSettings'

type IntegrationSettingsProps = {
  type: OrgCredentialType
  selectedCredential: OrgCredential
  showScopeList: boolean
}

export default function IntegrationSettings({
  type,
  selectedCredential,
  showScopeList,
}: IntegrationSettingsProps) {
  const {
    contentRef,
    dragDividerRef,
    dragDividerContainerRef,
    resizableContainerWidth,
    bind,
  } = useHorizontalDrag({
    identifier: 'integration-settings-scope-list-width',
    defaultWidth: 320,
    minLeftSideWidth: 180,
    minRightSideWidth: 200,
  })

  const requiresCompanySettings = type.supports.companies === 'required'

  const { organizations, isFetchingAll, queryKey } = useAllOrganizations({
    filters: {
      self_and_descendants_for_id: selectedCredential.org_id,
      is_relay_data_provider: 'true',
    },
    refetchOnWindowFocus: false,
    enabled: requiresCompanySettings,
  })

  const allowedConfiguration = type.allowed_configuration
  const allowsVendorSettings = allowedConfiguration.some((config) =>
    config.startsWith('vendor_'),
  )
  const allowsPaymentMethodsSettings =
    allowedConfiguration.includes('payment_cards')

  const [settingsOrgId, setSettingsOrgId] = useState(
    requiresCompanySettings ? undefined : selectedCredential.org_id,
  )

  useEffect(() => {
    if (requiresCompanySettings) {
      setSettingsOrgId(organizations[0]?.id || selectedCredential.org_id)
    } else {
      setSettingsOrgId(selectedCredential.org_id)
    }
  }, [organizations, requiresCompanySettings, selectedCredential.org_id])

  const { isAuthorized: canEditOrgs } = useStytchIsAuthorized('pd.org', 'edit')
  const addCompanyOverlay = useOverlay()

  if (!allowsVendorSettings && !allowsPaymentMethodsSettings) {
    return (
      <Typography variant="body2" color="text.secondary">
        No settings available for this integration.
      </Typography>
    )
  }

  const authenticated = isAuthenticated({ ...selectedCredential, type })
  const selectedOrganization = organizations.find(
    (organization) => organization.id === settingsOrgId,
  )

  return (
    <CredentialTypeProvider credentialType={type}>
      <Stack
        ref={contentRef}
        direction={requiresCompanySettings && showScopeList ? 'row' : 'column'}
        spacing={2}
        sx={{
          position: 'relative',
        }}
      >
        {requiresCompanySettings && showScopeList && (
          <Stack ref={dragDividerContainerRef} direction="row">
            <Box sx={{ width: resizableContainerWidth }}>
              <Typography
                variant="body2"
                sx={{ pl: 1.25, pb: 1 }}
                color="text.secondary"
              >
                Companies
              </Typography>
              {isFetchingAll ? (
                <Typography variant="body2" color="text.secondary">
                  Loading organizations...
                </Typography>
              ) : (
                <>
                  <List dense>
                    {organizations.map((organization) => (
                      <ListItem key={organization.id} disablePadding>
                        <ListItemButton
                          selected={settingsOrgId === organization.id}
                          onClick={() => setSettingsOrgId(organization.id)}
                        >
                          <ListItemText>
                            {organization.name}
                            {organization.code && (
                              <Typography
                                component="span"
                                variant="body2"
                                color="textSecondary"
                              >
                                {' '}
                                ({organization.code})
                              </Typography>
                            )}
                          </ListItemText>
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {canEditOrgs && (
                      <ListItem disablePadding>
                        <Button
                          startIcon={<Add />}
                          size="small"
                          sx={{ ml: 1, mt: 0.5 }}
                          onClick={addCompanyOverlay.open}
                          variant="text"
                        >
                          New Company
                        </Button>
                      </ListItem>
                    )}
                  </List>

                  {canEditOrgs && (
                    <AddCompanyDialog
                      overlay={addCompanyOverlay}
                      parentOrgId={selectedCredential.org_id}
                      listQueryKey={queryKey}
                      onCreate={setSettingsOrgId}
                    />
                  )}
                </>
              )}
            </Box>

            <Box
              ref={dragDividerRef}
              sx={{
                width: 4,
                flexShrink: 0,
                transition: '0.1s',
                cursor: 'ew-resize',
                borderRadius: 40,
                '&:hover': {
                  background: (theme) => theme.palette.primary.main,
                },
              }}
              {...bind()}
            />
          </Stack>
        )}

        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={2}>
          {requiresCompanySettings && settingsOrgId && (
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ mb: '-8px !important' }}
            >
              {selectedOrganization?.name || 'Unknown'}
            </Typography>
          )}

          {allowsVendorSettings && settingsOrgId && (
            <VendorSettings
              selectedCredential={selectedCredential}
              settingsOrgId={settingsOrgId}
              isAuthenticated={authenticated}
            />
          )}
          {allowsPaymentMethodsSettings && settingsOrgId && (
            <PaymentMethodsSettings
              selectedCredential={selectedCredential}
              settingsOrgId={settingsOrgId}
              isAuthenticated={authenticated}
            />
          )}
        </Stack>
      </Stack>
    </CredentialTypeProvider>
  )
}
